import {
  fetchMessagesForConversationIdsInRange,
  getConversationIdsForPatients,
  getConversationIdToPatientIdMap,
} from './dashboardService';

const HOUR_MS = 3_600_000;

/** A message authored by a patient. */
export function isPatientMessage(m) {
  return m?.senderRole === 'patient' || m?.senderType === 'patient' || m?.senderId === m?.patientId;
}

/**
 * A message authored by a human care-app user (non-patient), excluding
 * automated system messages and bulk-broadcast messages.
 */
export function isHumanCareTeamMessage(m) {
  if (!m) return false;
  if (isPatientMessage(m)) return false;
  if (m.senderRole !== 'care manager') return false;
  if (m.isSystemMessage === true) return false;
  if (m.senderId === 'system' || m.senderType === 'system') return false;
  if (m.bulkJobId) return false;
  return true;
}

function toDate(v) {
  if (!v) return null;
  if (v instanceof Date) return v;
  if (typeof v.toDate === 'function') return v.toDate();
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

function median(sortedNums) {
  if (!sortedNums.length) return null;
  const m = Math.floor(sortedNums.length / 2);
  return sortedNums.length % 2 ? sortedNums[m] : (sortedNums[m - 1] + sortedNums[m]) / 2;
}

function localDayKey(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Build the ordered list of calendar-day buckets between start and end (inclusive),
 * each pre-seeded with zero counts.
 */
function buildDayScaffold(start, end) {
  const days = [];
  const cursor = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const last = new Date(end.getFullYear(), end.getMonth(), end.getDate());
  while (cursor <= last) {
    days.push({
      key: localDayKey(cursor),
      dayOfMonth: cursor.getDate(),
      date: new Date(cursor),
      label: `${cursor.getMonth() + 1}/${cursor.getDate()}`,
      patient: 0,
      careTeam: 0,
    });
    cursor.setDate(cursor.getDate() + 1);
    // Guard against runaway ranges.
    if (days.length > 366) break;
  }
  return days;
}

/**
 * Fetch all in-scope messages for the patients in [start, end].
 * Includes both current care-team thread ids and legacy conversation ids.
 */
export async function fetchMessagesForPatientsInRange(patients, start, end) {
  const conversationIdSet = getConversationIdsForPatients(patients || []);
  const conversationIds = [...conversationIdSet];
  if (!conversationIds.length) {
    return { messages: [], conversationIdSet, conversationIdToPatientId: new Map() };
  }
  const messages = await fetchMessagesForConversationIdsInRange(conversationIds, start, end);
  const conversationIdToPatientId = getConversationIdToPatientIdMap(patients || []);
  return { messages, conversationIdSet, conversationIdToPatientId };
}

function weekStartKey(date) {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  d.setDate(d.getDate() - d.getDay()); // Sunday-anchored week.
  return localDayKey(d);
}

/**
 * Auto-scale the daily buckets into a chart series so multi-month periods stay
 * readable: daily (<= ~45 days), weekly (<= ~210 days), otherwise monthly.
 * @param {Array<{date: Date, label: string, patient: number, careTeam: number}>} days
 * @returns {{ granularity: 'day'|'week'|'month', data: Array<{label:string, patient:number, careTeam:number}> }}
 */
function buildChartSeries(days) {
  if (days.length <= 45) {
    return {
      granularity: 'day',
      data: days.map((d) => ({ label: d.label, patient: d.patient, careTeam: d.careTeam })),
    };
  }
  const monthly = days.length > 210;
  const buckets = new Map();
  const order = [];
  days.forEach((d) => {
    let key;
    let label;
    if (monthly) {
      key = `${d.date.getFullYear()}-${d.date.getMonth()}`;
      label = d.date.toLocaleDateString(undefined, { month: 'short', year: '2-digit' });
    } else {
      key = weekStartKey(d.date);
      const [, m, day] = key.split('-').map(Number);
      label = `${m}/${day}`;
    }
    if (!buckets.has(key)) {
      buckets.set(key, { label, patient: 0, careTeam: 0 });
      order.push(key);
    }
    const b = buckets.get(key);
    b.patient += d.patient;
    b.careTeam += d.careTeam;
  });
  return {
    granularity: monthly ? 'month' : 'week',
    data: order.map((k) => buckets.get(k)),
  };
}

/**
 * Compute the full Messages report dataset from a list of message docs.
 *
 * @param {Array} messages    message docs (createdAt/readAt already Date-coerced by the fetch layer)
 * @param {Set}   inScopeIds  conversationIds considered in-scope
 * @param {{start: Date, end: Date}} range
 * @returns dataset consumed by the report page
 */
export function buildMessageReport(messages, inScopeIds, range) {
  const { start, end } = range;
  const days = buildDayScaffold(start, end);
  const dayByKey = new Map(days.map((d) => [d.key, d]));

  let patientTotal = 0;
  let careTeamTotal = 0;
  const patientsWhoMessaged = new Set();
  const ackDeltas = []; // ms, patient message createdAt -> readAt

  const inScope = (m) => !inScopeIds || inScopeIds.has(m.conversationId);

  messages.forEach((m) => {
    if (!inScope(m)) return;
    const created = toDate(m.createdAt);
    if (!created) return;
    const bucket = dayByKey.get(localDayKey(created));

    if (isPatientMessage(m)) {
      patientTotal += 1;
      if (bucket) bucket.patient += 1;
      if (m.patientId) patientsWhoMessaged.add(m.patientId);
      const readAt = toDate(m.readAt);
      if (readAt && (!end || readAt <= end)) {
        const delta = readAt.getTime() - created.getTime();
        if (delta >= 0) ackDeltas.push(delta);
      }
    } else if (isHumanCareTeamMessage(m)) {
      careTeamTotal += 1;
      if (bucket) bucket.careTeam += 1;
    }
  });

  ackDeltas.sort((a, b) => a - b);
  const ackCount = ackDeltas.length;
  const ackMedianMs = median(ackDeltas);

  const ackBuckets = [
    { key: 'lt1h', label: '< 1 hr', count: 0 },
    { key: '1to4h', label: '1–4 hr', count: 0 },
    { key: '4to24h', label: '4–24 hr', count: 0 },
    { key: 'gt24h', label: '> 24 hr', count: 0 },
  ];
  ackDeltas.forEach((d) => {
    if (d < HOUR_MS) ackBuckets[0].count += 1;
    else if (d < 4 * HOUR_MS) ackBuckets[1].count += 1;
    else if (d < 24 * HOUR_MS) ackBuckets[2].count += 1;
    else ackBuckets[3].count += 1;
  });

  // Patient messages still awaiting acknowledgement at the end of the period.
  const unacknowledgedPatientMessages = messages.filter((m) => {
    if (!inScope(m) || !isPatientMessage(m)) return false;
    const readAt = toDate(m.readAt);
    return readAt == null || (end && readAt > end);
  }).length;

  return {
    totals: {
      patient: patientTotal,
      careTeam: careTeamTotal,
      total: patientTotal + careTeamTotal,
    },
    patientsWhoMessaged: patientsWhoMessaged.size,
    daily: days,
    chart: buildChartSeries(days),
    peakDay: days.reduce((best, d) => {
      const t = d.patient + d.careTeam;
      return t > (best ? best.patient + best.careTeam : -1) ? d : best;
    }, null),
    acknowledgement: {
      count: ackCount,
      medianMs: ackMedianMs,
      buckets: ackBuckets,
      unacknowledged: unacknowledgedPatientMessages,
    },
  };
}

/** Human-readable duration from milliseconds. */
export function formatDurationMs(ms) {
  if (ms == null || Number.isNaN(ms) || ms < 0) return '—';
  if (ms < 90_000) return `${Math.round(ms / 1000)} sec`;
  if (ms < HOUR_MS) return `${(ms / 60_000).toFixed(0)} min`;
  if (ms < 24 * HOUR_MS) return `${(ms / HOUR_MS).toFixed(1)} hr`;
  return `${(ms / (24 * HOUR_MS)).toFixed(1)} days`;
}

/**
 * Resolve display info for a single client (header band) from loaded
 * clients + clientSites. Returns null when no specific client is selected.
 */
export function resolveClientHeaderInfo(clientId, clients, clientSites) {
  if (!clientId) return null;
  const client = (clients || []).find((c) => c.id === clientId);
  if (!client) return null;
  const sites = (clientSites || []).filter((s) => String(s.clientId || '') === String(clientId));
  const cityStateZip = [client.clientCity, client.clientState].filter(Boolean).join(', ');
  return {
    id: client.id,
    name: client.clientName || client.clientShortName || client.id,
    shortName: client.clientShortName || '',
    logoUrl: client.logoUrl || '',
    addressLine1: client.clientStreet1 || '',
    addressLine2: [cityStateZip, client.clientZip].filter(Boolean).join(' '),
    siteCount: sites.length,
  };
}
